import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Circle } from "lucide-react";
import { PageTitle, Stat, Badge, typeLabel } from "@/components/app/ui";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/app/workspace").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="h-64 rounded-2xl bg-muted animate-pulse" />;
  const c = d.company;
  const steps = [
    ["Complete your Brand Profile", d.brand_complete, "/app/brand"],
    ["Add company facts to the Knowledge Base", d.knowledge_count > 0, "/app/knowledge"],
    ["Generate your first content", d.recent.length > 0, "/app/studio"],
    ["Approve a draft", d.counts.approved + d.counts.published + d.counts.scheduled > 0, "/app/approvals"],
  ];
  return (
    <div data-testid="dashboard-page">
      <PageTitle title={`Hi ${user?.name?.split(" ")[0] || "there"}`} copy={`${c.name} · ${c.plan_name}${c.plan_expires_at ? ` · renews/expires ${new Date(c.plan_expires_at).toLocaleDateString()}` : ""}`}>
        <Link to="/app/studio" className="btn-brand" data-testid="dash-create-btn">Create content <ArrowRight className="h-4 w-4" /></Link>
      </PageTitle>
      {c.plan === "free" && (
        <div className="mb-8 rounded-2xl bg-brand p-6 text-white flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="dash-upgrade-banner">
          <div><div className="font-display text-xl font-bold">You're on the Explorer plan</div><p className="text-white/80 text-sm mt-1">Upgrade to unlock the content calendar, publishing, campaigns and analytics.</p></div>
          <Link to="/app/billing" className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#4f46e5]">See plans</Link>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="AI credits" value={c.credits.toLocaleString()} color="#7c3aed" testId="stat-credits" hint={`${d.credits_used.toLocaleString()} used`} />
        <Stat label="Pending approval" value={d.counts.pending_approval} color="#f59e0b" testId="stat-pending" />
        <Stat label="Scheduled / published" value={d.counts.scheduled + d.counts.published} color="#10b981" testId="stat-published" />
        <Stat label="Planned ideas" value={d.counts.planned} color="#06b6d4" testId="stat-planned" />
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5 card-box p-6">
          <h3 className="font-semibold text-lg">Getting started</h3>
          <ul className="mt-4 space-y-2">
            {steps.map(([t, ok, to]) => (
              <li key={t}><Link to={to} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-foreground/5">{ok ? <CheckCircle2 className="h-5 w-5 text-[#10b981]" /> : <Circle className="h-5 w-5 text-muted-foreground" />}<span className={`text-sm ${ok ? "line-through text-muted-foreground" : ""}`}>{t}</span></Link></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7 card-box p-6">
          <div className="flex justify-between items-center"><h3 className="font-semibold text-lg">Recent content</h3><Link to="/app/approvals" className="text-sm text-[#7c3aed] font-semibold">View all</Link></div>
          <div className="mt-4 space-y-2">
            {d.recent.length === 0 && <p className="text-sm text-muted-foreground py-6">No content yet. Head to the Content Studio to generate your first draft.</p>}
            {d.recent.map((x) => (
              <div key={x.id} className="flex items-center justify-between gap-3 rounded-xl border px-4 py-3">
                <div className="min-w-0"><div className="text-sm font-medium truncate">{x.title}</div><div className="text-xs text-muted-foreground">{typeLabel(x.type)} · {x.channel}</div></div>
                <Badge s={x.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
